import { z } from "zod"

export const COST_CLASSIFICATION = {
  unknown: "unknown",
  placeholder: "placeholder",
  measured: "measured",
} as const
export const COST_CAPTURE_ACTION = { start: "start", stop: "stop" } as const
export const COST_EVIDENCE = { unknown: "unknown", verified: "verified" } as const
export const COST_TAX_BASIS = { unknown: "unknown", net: "net", gross: "gross" } as const
export const COST_RECORD_KIND = { cached: "cached_start", observed: "source_observation" } as const
export const COST_MOVEMENT_REASON = {
  first: "first_observation",
  unknown: "unknown_amount",
  placeholder: "placeholder_amount",
  unverified: "unverified_monetary_basis",
  changedBasis: "different_monetary_basis",
  comparable: "comparable",
} as const
export const COST_COVERAGE_LIMIT =
  "Observations show what Nova received. Successful checks do not prove every upstream change was captured. Source versions and mirror timestamps are not cost-effective dates."
const decimal = z.string().regex(/^-?\d+(?:\.\d+)?$/)
const instant = z.iso.datetime({ offset: true })
const sourceCheck = z.object({ finishedAt: instant, ok: z.boolean(), mode: z.string() })
export const productCostEvidenceSchema = z.object({
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .nullable(),
  taxBasis: z.enum(COST_TAX_BASIS),
  status: z.enum(COST_EVIDENCE),
  reference: z.string().min(1).nullable(),
})
export type ProductCostEvidence = z.infer<typeof productCostEvidenceSchema>
export const productCostObservationSchema = z.object({
  id: z.number().int().positive(),
  amount: decimal.nullable(),
  rawAmount: z.string().nullable(),
  classification: z.enum(COST_CLASSIFICATION),
  evidence: productCostEvidenceSchema,
  kind: z.enum(COST_RECORD_KIND),
  source: z.string(),
  observedAt: instant,
  recordedAt: instant,
  sourceVersionAt: instant.nullable(),
  mirrorSyncedAt: instant.nullable(),
  effectiveAt: instant.nullable(),
  parentSku: z.string().nullable(),
})
export type ProductCostObservation = z.infer<typeof productCostObservationSchema>
export const productCostMovementSchema = z.object({
  predecessorId: z.number().int().positive().nullable(),
  reason: z.enum(COST_MOVEMENT_REASON),
  amount: decimal.nullable(),
  percentage: decimal.nullable(),
})
export type ProductCostMovement = z.infer<typeof productCostMovementSchema>
const date = z.iso.date()
export const productCostHistoryQuerySchema = z
  .object({
    store: z.string().min(1).max(100),
    from: date.optional(),
    to: date.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
    cursor: z.string().max(3000).optional(),
  })
  .refine((value) => !value.from || !value.to || value.from <= value.to, {
    message: "from must not follow to (inclusive UTC dates)",
  })
export type ProductCostHistoryQuery = z.infer<typeof productCostHistoryQuerySchema>
export const productCostHistoryResponseSchema = z.object({
  simpleSku: z.string(),
  store: z.string(),
  items: z.array(productCostObservationSchema.extend({ movement: productCostMovementSchema })),
  nextCursor: z.string().nullable(),
  boundaryId: z.number().int().nonnegative(),
  coverage: z.object({
    baselineRecorded: z.boolean(),
    lastSuccessfulCheckAt: instant.nullable(),
    lastSuccessfulSourceCheckAt: instant.nullable(),
    lastSourceCheck: sourceCheck.nullable(),
    sourceVersionAt: instant.nullable(),
    catalogPresent: z.boolean(),
    captureActive: z.boolean(),
    intervals: z.array(
      z.object({ startedAt: instant, endedAt: instant.nullable(), reason: z.string() })
    ),
    gaps: z.array(
      z.object({ startedAt: instant, endedAt: instant.nullable(), reason: z.string() })
    ),
    coverageTruncated: z.boolean(),
    limitation: z.string(),
  }),
})
export type ProductCostHistoryResponse = z.infer<typeof productCostHistoryResponseSchema>
export const productCostIdentitiesResponseSchema = z.object({
  items: z.array(
    z.object({
      simpleSku: z.string(),
      store: z.string(),
      parentSku: z.string().nullable(),
      catalogPresent: z.boolean(),
    })
  ),
  truncated: z.boolean(),
})
export type ProductCostIdentitiesResponse = z.infer<typeof productCostIdentitiesResponseSchema>
export const productCostCoverageResponseSchema = z.object({
  identities: z.number(),
  recordedIdentities: z.number(),
  unresolvedIdentities: z.number(),
  observations: z.number(),
  lastSuccessfulCheckAt: instant.nullable(),
  captureActive: z.boolean(),
  sourceChecks: z.array(
    sourceCheck.extend({ store: z.string(), lastSuccessfulSourceCheckAt: instant.nullable() })
  ),
  baseline: z.object({ complete: z.boolean() }),
  limitation: z.string(),
})
export type ProductCostCoverageResponse = z.infer<typeof productCostCoverageResponseSchema>
export const productCostBaselineBodySchema = z.object({
  limit: z.number().int().min(1).max(1000).default(250),
})
export const productCostCaptureBodySchema = z.discriminatedUnion("action", [
  z.object({
    action: z.literal(COST_CAPTURE_ACTION.start),
    reason: z.string().min(1).max(1000),
    compatibleWritersDrained: z.literal(true),
  }),
  z.object({ action: z.literal(COST_CAPTURE_ACTION.stop), reason: z.string().min(1).max(1000) }),
])
